import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';

interface SiatCompanyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payload: any, isEdit: boolean) => Promise<void>;
    initialData?: any; // Si viene con datos, es modo Edición
}

export const SiatCompanyModal = ({ isOpen, onClose, onSave, initialData }: SiatCompanyModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        issuerNit: '',
        issuerBusinessName: '',
        headOfficeMunicipality: '',
        economicActivityCode: '',
        digitalSignaturePath: '',
        digitalSignatureExpiration: '',
        digitalSignaturePassword: '',
        siatEnvironmentToken: ''
    });

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                // Modo Edición: Llenar campos, pero vaciar los secretos para que el usuario decida si los cambia
                setFormData({
                    issuerNit: initialData.issuerNit || '',
                    issuerBusinessName: initialData.issuerBusinessName || '',
                    headOfficeMunicipality: initialData.headOfficeMunicipality || '',
                    economicActivityCode: initialData.economicActivityCode || '',
                    digitalSignaturePath: initialData.digitalSignaturePath || '',
                    digitalSignatureExpiration: initialData.digitalSignatureExpiration ? initialData.digitalSignatureExpiration.split('T')[0] : '',
                    digitalSignaturePassword: '',
                    siatEnvironmentToken: ''
                });
            } else {
                // Modo Creación: Limpiar todo
                setFormData({
                    issuerNit: '', issuerBusinessName: '', headOfficeMunicipality: '', economicActivityCode: '',
                    digitalSignaturePath: '', digitalSignatureExpiration: '', digitalSignaturePassword: '', siatEnvironmentToken: ''
                });
            }
        }
    }, [isOpen, initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        const payload = {
            issuerNit: formData.issuerNit ? Number(formData.issuerNit) : null,
            issuerBusinessName: formData.issuerBusinessName || null,
            headOfficeMunicipality: formData.headOfficeMunicipality || null,
            economicActivityCode: formData.economicActivityCode ? Number(formData.economicActivityCode) : null,
            digitalSignaturePath: formData.digitalSignaturePath || null,
            digitalSignatureExpiration: formData.digitalSignatureExpiration || null,
            // Regla de secretos: si está vacío en edición, enviar null. En creación, enviar el string (aunque esté vacío).
            digitalSignaturePassword: formData.digitalSignaturePassword ? formData.digitalSignaturePassword : (initialData ? null : ''),
            siatEnvironmentToken: formData.siatEnvironmentToken ? formData.siatEnvironmentToken : (initialData ? null : '')
        };

        try {
            await onSave(payload, !!initialData);
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-900">
                        {initialData ? 'Editar Empresa y Credenciales' : 'Registrar Empresa SIAT'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <form id="company-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        <div className="md:col-span-2"><h4 className="text-sm font-semibold text-blue-900 border-b pb-1">Datos Generales</h4></div>
                        
                        <div>
                            <label className="block mb-1 text-xs font-medium text-gray-700">NIT Emisor</label>
                            <input type="number" name="issuerNit" value={formData.issuerNit} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-gray-700">Razón Social</label>
                            <input type="text" name="issuerBusinessName" value={formData.issuerBusinessName} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-gray-700">Municipio Casa Matriz</label>
                            <input type="text" name="headOfficeMunicipality" value={formData.headOfficeMunicipality} onChange={handleChange} placeholder="Ej. Cochabamba" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-gray-700">Cód. Actividad Económica</label>
                            <input type="number" name="economicActivityCode" value={formData.economicActivityCode} onChange={handleChange} placeholder="Ej. 620100" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                        </div>

                        <div className="md:col-span-2 mt-4"><h4 className="text-sm font-semibold text-blue-900 border-b pb-1">Firma Digital (ADSIB)</h4></div>

                        <div className="md:col-span-2">
                            <label className="block mb-1 text-xs font-medium text-gray-700">Ruta del Certificado (.p12/.pfx)</label>
                            <input type="text" name="digitalSignaturePath" value={formData.digitalSignaturePath} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-gray-700">Fecha de Vencimiento</label>
                            <input type="date" name="digitalSignatureExpiration" value={formData.digitalSignatureExpiration} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-gray-700">Contraseña del Certificado {initialData && <span className="text-gray-400 font-normal">(Dejar en blanco para no cambiar)</span>}</label>
                            <input type="password" name="digitalSignaturePassword" value={formData.digitalSignaturePassword} onChange={handleChange} placeholder={initialData ? '••••••••' : 'Ingrese contraseña'} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required={!initialData} />
                        </div>

                        <div className="md:col-span-2 mt-4"><h4 className="text-sm font-semibold text-blue-900 border-b pb-1">Entorno SIAT</h4></div>

                        <div className="md:col-span-2">
                            <label className="block mb-1 text-xs font-medium text-gray-700">Token de Delegación {initialData && <span className="text-gray-400 font-normal">(Dejar en blanco para no cambiar)</span>}</label>
                            <input type="text" name="siatEnvironmentToken" value={formData.siatEnvironmentToken} onChange={handleChange} placeholder={initialData ? '••••••••••••••••' : 'Ingrese el token generado por el SIN'} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required={!initialData} />
                        </div>
                    </form>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        Cancelar
                    </button>
                    <button type="submit" form="company-form" disabled={isLoading} className="flex items-center justify-center min-w-[120px] px-4 py-2 text-sm font-medium text-white bg-[#1e3a8a] rounded-lg hover:bg-blue-900 disabled:opacity-70">
                        {isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                        {initialData ? 'Guardar Cambios' : 'Registrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};